import React, { memo, useCallback, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { LoadableLazy } from '@confluence/loadable';

import { ANALYTICS_SOURCE } from './analytics.ts';
import { ConnectionRowBody } from './connection-row/ConnectionRowBody.tsx';
import { ConnectionRowConnectButton } from './connection-row/ConnectionRowConnectButton.tsx';
import type { ConnectionItemHandler, RefreshLinkUrlHandler } from './connectionTypes.ts';
import { ConnectionsByLineRowActions } from './ConnectionsByLineRowActions.tsx';
import { useConnectionRowActions } from './hooks/useConnectionRowActions.ts';
import { useConnectionRowNavigation } from './hooks/useConnectionRowNavigation.ts';
import type { ConnectionRowInteractionHandler } from './utils/connectionRowActionTypes.ts';
import type { EnhancedSmartLink, LinkDirection } from './utils/smartLinkTypes.ts';

const UnconnectedConnectorHoverCardLoadable = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-UnconnectedConnectorHoverCard" */ './UnconnectedConnectorHoverCard.tsx'
			)
		).UnconnectedConnectorHoverCard,
});

const styles = cssMap({
	// The body button and the trailing buttons are siblings, so none is nested interactive.
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		width: '100%',
		minHeight: '40px',
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
		boxSizing: 'border-box',
		borderRadius: 'inherit',
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

export type ConnectionsByLineMenuItemProps = {
	item: EnhancedSmartLink;
	refreshLinkUrl?: RefreshLinkUrlHandler;
	linkDirection: LinkDirection;
	/**
	 * The cc-smarts one-liner about this link. When present it replaces the metadata
	 * line so the row is title + one-liner. The byline popup and Appears in / Links
	 * to rows pass none and keep title + metadata.
	 */
	aiSummary?: string;
	onConnectSuccess?: () => void;
	onHover?: ConnectionItemHandler;
	onNavigate?: () => void;
	/** Called immediately before the row invokes its Smart Link preview action. */
	onPreviewOpen?: () => void;
	/**
	 * Switches the Teamwork Graph modal to Rovo chat, seeded with the given prefilled
	 * prompt.
	 */
	onAskRovo?: (prompt?: string) => void;
	/**
	 * Title-only row: hides the description and AI summary. Every popup menu section
	 * uses this; the preview panel keeps the two-line row.
	 */
	compact?: boolean;
	/**
	 * Static row: suppresses every hover affordance — the Connect button, the trailing
	 * row-action cluster, and the unconnected-connector hover card — and never reports a
	 * hover. Every popup menu section uses this so its rows stay a plain list; the
	 * preview panel is where those actions live.
	 */
	disableHover?: boolean;
	/** Called on every reader interaction with the row: a body click or a row action. */
	onInteraction?: ConnectionRowInteractionHandler;
	/**
	 * Source for the row's click and scroll events. Defaults to `ANALYTICS_SOURCE`; the embedded
	 * menu passes its own so its events stay separate from the default experience.
	 */
	analyticsSource?: string;
};

const ConnectionsByLineMenuItemInner = ({
	item,
	refreshLinkUrl,
	linkDirection,
	aiSummary,
	onConnectSuccess,
	onHover,
	onNavigate,
	onPreviewOpen,
	onAskRovo,
	compact,
	disableHover,
	onInteraction,
	analyticsSource = ANALYTICS_SOURCE,
}: ConnectionsByLineMenuItemProps): React.JSX.Element => {
	const [isRowHovered, setIsRowHovered] = useState(false);
	const [isRowFocused, setIsRowFocused] = useState(false);
	const isGranted = item.access === 'granted';
	const rowActions = useConnectionRowActions(item, onPreviewOpen, onAskRovo);

	const handleRowClick = useConnectionRowNavigation({
		item,
		linkDirection,
		analyticsSource,
		onInteraction,
		onNavigate,
	});

	const handleMouseEnter = useCallback(() => {
		if (disableHover) {
			return;
		}
		setIsRowHovered(true);
		onHover?.(item);
	}, [disableHover, onHover, item]);

	const handleMouseLeave = useCallback(() => {
		setIsRowHovered(false);
	}, []);

	const handleFocus = useCallback(() => {
		if (disableHover) {
			return;
		}
		setIsRowFocused(true);
	}, [disableHover]);

	const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
		// Tabbing from the body to a trailing button must not hide it.
		if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
			setIsRowFocused(false);
		}
	}, []);

	const showAffordances = !disableHover && (isRowHovered || isRowFocused);

	// `forbidden` means the user is authenticated to the provider but lacks
	// permission to this specific resource (e.g. a private Google Drive doc) —
	// connecting an account can't grant access, so don't offer Connect. (Note:
	// `meta.visibility` is unreliable here: accessible docs report 'restricted'
	// while the private one reports 'not_found', so we key on `access`.)
	const showConnectAction =
		!isGranted &&
		item.access !== 'forbidden' &&
		item.authProviders?.length > 0 &&
		// Without a refresher there is nothing to update the row after a successful
		// auth, so the Connect flow would dead-end — don't offer it at all.
		Boolean(refreshLinkUrl);

	const row = (ref?: React.Ref<HTMLDivElement>) => (
		<Box
			ref={ref}
			xcss={styles.row}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			<ConnectionRowBody
				item={item}
				linkDirection={linkDirection}
				compact={compact}
				aiSummary={aiSummary}
				onClick={handleRowClick}
			/>
			{showConnectAction && refreshLinkUrl && showAffordances ? (
				<ConnectionRowConnectButton
					item={item}
					refreshLinkUrl={refreshLinkUrl}
					onConnectSuccess={onConnectSuccess}
				/>
			) : null}
			{showAffordances && rowActions.length > 0 ? (
				<ConnectionsByLineRowActions item={item} actions={rowActions} onAction={onInteraction} />
			) : null}
		</Box>
	);

	// `disableHover` rows never open the hover card, so skip the lazy wrapper entirely
	// rather than mount it with a permanently-closed `isOpen`.
	if (showConnectAction && !disableHover) {
		return (
			<UnconnectedConnectorHoverCardLoadable item={item} isOpen={isRowHovered}>
				{(triggerProps: TriggerProps) => row(triggerProps.ref as React.Ref<HTMLDivElement>)}
			</UnconnectedConnectorHoverCardLoadable>
		);
	}

	return row();
};

export const ConnectionsByLineMenuItem: React.MemoExoticComponent<
	typeof ConnectionsByLineMenuItemInner
> = memo(ConnectionsByLineMenuItemInner);
